/**
 * admin/src/pages/AnalyticsPage.tsx
 * Analytics Dashboard v3.0 — SVG Area Chart + Heatmap + Better UX
 */

import { useState } from 'react';
import { useAnalyticsAll, useAnalyticsTop } from '../hooks/useAnalytics';
import type { TopStudentSort } from '../hooks/useAnalytics';
import {
  TierBadge,
  LoadingSpinner,
  ErrorBanner,
  formatVND,
} from '../components/ui/index';
import type {
  AnalyticsDailyPoint,
  AnalyticsGrowthPoint,
  AnalyticsRevenuePoint,
  AnalyticsTopStudent,
} from '../types/admin';

// ─── SVG Area Chart ──────────────────────────────────────────────────────────

function AreaChart({
  data,
  color = 'var(--accent)',
  height = 140,
}: {
  data: number[];
  color?: string;
  height?: number;
}) {
  if (data.length === 0) return null;
  const max = Math.max(...data, 1);
  const W = 600;
  const H = height;
  const PAD = { top: 8, right: 4, bottom: 4, left: 0 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const pts = data.map((v, i) => ({
    x: PAD.left + (i / (data.length - 1)) * chartW,
    y: PAD.top + chartH - (v / max) * chartH,
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${pts[pts.length - 1].x.toFixed(1)},${(PAD.top + chartH).toFixed(1)} L${pts[0].x.toFixed(1)},${(PAD.top + chartH).toFixed(1)} Z`;

  const gradId = `grad-${Math.random().toString(36).slice(2)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Last point dot */}
      <circle
        cx={pts[pts.length - 1].x}
        cy={pts[pts.length - 1].y}
        r="4"
        fill={color}
        stroke="var(--bg-card)"
        strokeWidth="2"
      />
    </svg>
  );
}

// ─── Enhanced Bar Chart ──────────────────────────────────────────────────────

function BarChart({
  data,
  getValue,
  getLabel,
  color = 'var(--accent)',
  showLabels = false,
}: {
  data: { key: string; value: number; label: string }[];
  getValue: (d: { key: string; value: number; label: string }) => number;
  getLabel: (d: { key: string; value: number; label: string }) => string;
  color?: string;
  showLabels?: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(...data.map(getValue), 1);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          height: 120,
          width: '100%',
        }}
      >
        {data.map((d, i) => {
          const h = Math.max((getValue(d) / max) * 110, getValue(d) > 0 ? 4 : 0);
          const isHovered = hovered === i;
          return (
            <div
              key={i}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {isHovered && (
                <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-1)',
                  whiteSpace: 'nowrap',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '2px 6px',
                }}>
                  {getValue(d).toLocaleString('vi-VN')}
                </div>
              )}
              <div
                title={`${getLabel(d)}: ${getValue(d).toLocaleString('vi-VN')}`}
                style={{
                  width: '100%',
                  height: h,
                  background: isHovered ? color : `${color}bb`,
                  borderRadius: '3px 3px 0 0',
                  cursor: 'default',
                  transition: 'all 0.15s ease',
                  transform: isHovered ? 'scaleY(1.03)' : 'scaleY(1)',
                  transformOrigin: 'bottom',
                  minWidth: 2,
                }}
              />
            </div>
          );
        })}
      </div>
      {showLabels && (
        <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
          {data.map((d, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: 'var(--text-3)', overflow: 'hidden' }}>
              {getLabel(d).slice(-5)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stat Pill ───────────────────────────────────────────────────────────────

function StatPill({ label, value, trend }: { label: string; value: string | number; trend?: 'up' | 'down' }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-1)', letterSpacing: '-0.5px' }}>
        {typeof value === 'number' ? value.toLocaleString('vi-VN') : value}
        {trend && (
          <span style={{ fontSize: 14, marginLeft: 6, color: trend === 'up' ? 'var(--success)' : 'var(--danger)' }}>
            {trend === 'up' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const [days, setDays] = useState<14 | 30 | 90>(30);
  const [topBy, setTopBy] = useState<TopStudentSort>('total_stars');

  const { daily, growth, revenue, isLoading } = useAnalyticsAll(days);
  const { data: topStudents, isLoading: topLoading, error: topError } = useAnalyticsTop(topBy);

  const dailyPoints = daily.data as AnalyticsDailyPoint[];
  const growthPoints = growth.data as AnalyticsGrowthPoint[];

  const totalSessions = dailyPoints.reduce((s, d) => s + d.total_sessions, 0);
  const totalActiveStudents = dailyPoints.reduce((s, d) => s + d.active_students, 0);
  const avgSessionsPerDay = dailyPoints.length > 0 ? Math.round(totalSessions / dailyPoints.length) : 0;

  const sessionValues = dailyPoints.map(d => d.total_sessions);
  const activeValues = dailyPoints.map(d => d.active_students);

  // dailyBars kept for future bar view toggle
  // const dailyBars = dailyPoints.map(d => ({ key: d.date_key, value: d.total_sessions, label: d.date_key }));
  const growthBars = growthPoints.map(d => ({ key: d.week, value: d.new_users, label: `Tuần ${d.week}` }));

  const revenueByMonth = (revenue.data as AnalyticsRevenuePoint[]).reduce<
    Record<string, { basic: number; premium: number; family: number; total: number }>
  >((acc, point) => {
    if (!acc[point.month]) acc[point.month] = { basic: 0, premium: 0, family: 0, total: 0 };
    const key = point.plan as 'basic' | 'premium' | 'family';
    if (key === 'basic' || key === 'premium' || key === 'family') acc[point.month][key] += point.count;
    acc[point.month].total += point.revenue_estimate;
    return acc;
  }, {});
  const revenueMonths = Object.entries(revenueByMonth).sort(([a], [b]) => b.localeCompare(a));

  function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return <button className={`filter-chip${active ? ' active' : ''}`} onClick={onClick}>{children}</button>;
  }

  if (isLoading && dailyPoints.length === 0) {
    return (
      <div>
        <div className="page-header">
          <div className="page-header__left"><h1>Analytics</h1><p>Thống kê hoạt động nền tảng</p></div>
        </div>
        <div className="flex-center" style={{ padding: 80 }}><LoadingSpinner size={48} /></div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Analytics</h1>
          <p>Thống kê hoạt động · luyenthi.io.vn</p>
        </div>
        <div className="filter-chips">
          <FilterChip active={days === 14} onClick={() => setDays(14)}>14 ngày</FilterChip>
          <FilterChip active={days === 30} onClick={() => setDays(30)}>30 ngày</FilterChip>
          <FilterChip active={days === 90} onClick={() => setDays(90)}>90 ngày</FilterChip>
        </div>
      </div>

      {/* ─── Section 1: Activity Overview với Area Chart ── */}
      <div className="card card-lg" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>📊 Hoạt động theo ngày ({days} ngày)</h3>
          <div style={{ display: 'flex', gap: 32 }}>
            <StatPill label="Tổng sessions" value={totalSessions} />
            <StatPill label="HS active" value={totalActiveStudents} />
            <StatPill label="TB/ngày" value={avgSessionsPerDay} />
          </div>
        </div>

        {daily.error ? (
          <ErrorBanner message={daily.error} />
        ) : daily.isLoading ? (
          <div className="flex-center" style={{ height: 140 }}><LoadingSpinner /></div>
        ) : sessionValues.length === 0 ? (
          <div className="flex-center" style={{ height: 140, color: 'var(--text-3)', fontSize: 13 }}>Không có dữ liệu</div>
        ) : (
          <div>
            {/* Dual area: sessions vs active students */}
            <div style={{ position: 'relative', height: 140 }}>
              <AreaChart data={sessionValues} color="var(--accent)" height={140} />
              <div style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
                <AreaChart data={activeValues} color="var(--success)" height={140} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-3)' }}>
                <span style={{ width: 12, height: 3, background: 'var(--accent)', borderRadius: 99, display: 'inline-block' }} />
                Sessions
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-3)' }}>
                <span style={{ width: 12, height: 3, background: 'var(--success)', borderRadius: 99, display: 'inline-block' }} />
                Học sinh active
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ─── 2-column: Growth + Revenue ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Growth */}
        <div className="card card-lg">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>📈 Tăng trưởng Users</h3>
            {growthPoints.length > 0 && (
              <span style={{ fontSize: 13, color: 'var(--text-3)' }}>
                <strong style={{ color: 'var(--text-1)', fontWeight: 700 }}>
                  +{growthPoints.reduce((s, d) => s + d.new_users, 0).toLocaleString('vi-VN')}
                </strong> users
              </span>
            )}
          </div>
          {growth.error ? <ErrorBanner message={growth.error} />
          : growth.isLoading ? <div className="flex-center" style={{ height: 120 }}><LoadingSpinner /></div>
          : growthBars.length === 0 ? <div className="flex-center" style={{ height: 120, color: 'var(--text-3)', fontSize: 13 }}>Không có dữ liệu</div>
          : <BarChart data={growthBars} getValue={d => d.value} getLabel={d => d.label ?? d.key} color="#818cf8" showLabels />}
        </div>

        {/* Revenue */}
        <div className="card card-lg">
          <h3 style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 16 }}>💰 Doanh thu ước tính</h3>
          {revenue.error ? <ErrorBanner message={revenue.error} />
          : revenue.isLoading ? <div className="flex-center" style={{ height: 120 }}><LoadingSpinner /></div>
          : revenueMonths.length === 0 ? <div className="flex-center" style={{ height: 120, color: 'var(--text-3)', fontSize: 13 }}>Không có dữ liệu</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Tháng', 'Member', 'Premium', 'Family', 'Doanh thu'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', color: 'var(--text-3)', fontWeight: 600, textAlign: h === 'Doanh thu' ? 'right' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {revenueMonths.slice(0, 6).map(([month, data]) => (
                    <tr key={month} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                      <td style={{ padding: '8px', color: 'var(--text-1)', fontWeight: 500, textAlign: 'center' }}>{month}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {data.basic > 0 ? <span className="badge badge-basic">{data.basic}</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {data.premium > 0 ? <span className="badge badge-premium">{data.premium}</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {data.family > 0 ? <span className="badge badge-success">{data.family}</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', color: 'var(--text-1)', fontWeight: 700 }}>
                        {formatVND(data.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Leaderboard ── */}
      <div className="card card-lg">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>🏆 Bảng xếp hạng học sinh</h3>
          <div className="filter-chips">
            <FilterChip active={topBy === 'total_stars'} onClick={() => setTopBy('total_stars')}>⭐ Sao</FilterChip>
            <FilterChip active={topBy === 'current_streak'} onClick={() => setTopBy('current_streak')}>🔥 Streak</FilterChip>
            <FilterChip active={topBy === 'total_sessions'} onClick={() => setTopBy('total_sessions')}>📝 Sessions</FilterChip>
          </div>
        </div>

        {topError ? <ErrorBanner message={topError} />
        : topLoading ? <div className="flex-center" style={{ padding: 40 }}><LoadingSpinner /></div>
        : (topStudents as AnalyticsTopStudent[]).length === 0 ? <div className="flex-center" style={{ padding: 40, color: 'var(--text-3)', fontSize: 13 }}>Không có dữ liệu</div>
        : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['#', 'Tên', 'Phụ huynh', 'Tier', '⭐ Sao', '🔥 Streak', '📝 Sessions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', color: 'var(--text-3)', fontWeight: 600, textAlign: ['⭐ Sao', '🔥 Streak', '📝 Sessions'].includes(h) ? 'right' : 'left', whiteSpace: 'nowrap', fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(topStudents as AnalyticsTopStudent[]).map((student, index) => (
                  <tr key={student.id} style={{ borderBottom: '1px solid var(--border-soft)', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontWeight: index < 3 ? 800 : 400, fontSize: index < 3 ? 18 : 14 }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{student.display_name}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontSize: 12.5 }}>{student.parent_email}</td>
                    <td style={{ padding: '10px 12px' }}><TierBadge tier={student.parent_tier} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: topBy === 'total_stars' ? 'var(--accent)' : 'var(--text-1)', fontWeight: topBy === 'total_stars' ? 700 : 400 }}>
                      {student.total_stars.toLocaleString('vi-VN')}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: topBy === 'current_streak' ? '#f97316' : 'var(--text-1)', fontWeight: topBy === 'current_streak' ? 700 : 400 }}>
                      {student.current_streak}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: topBy === 'total_sessions' ? 'var(--success)' : 'var(--text-1)', fontWeight: topBy === 'total_sessions' ? 700 : 400 }}>
                      {student.total_sessions.toLocaleString('vi-VN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
